import { useEffect, useState } from 'react';
import { Laptop, Save } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { profileLoaded } from '@/features/auth/authSlice';
import {
  useChangePasswordMutation, useLazyMeQuery, useRevokeSessionMutation, useSessionsQuery,
  useUpdateProfileMutation,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatRelative } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, LoadingState, PageHeader,
  Pagination,
} from '@/components/ui';
import { AppearanceCard } from '@/components/AppearanceCard';
import { PrinterCard } from '@/components/PrinterCard';

export function ProfilePage() {
  const dispatch = useAppDispatch();
  const format = useFormat();
  const user = useAppSelector((state) => state.auth.user);

  const [updateProfile, profileState] = useUpdateProfileMutation();
  const [changePassword, passwordState] = useChangePasswordMutation();
  const [fetchMe] = useLazyMeQuery();
  const [devicePage, setDevicePage] = useState(1);
  const [deviceLimit, setDeviceLimit] = useState(25);
  const sessions = useSessionsQuery({ page: devicePage, limit: deviceLimit });
  const [revokeSession] = useRevokeSessionMutation();

  const [profile, setProfile] = useState({ firstName: '', lastName: '', phone: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  useEffect(() => {
    if (user) {
      setProfile({ firstName: user.firstName, lastName: user.lastName, phone: '' });
    }
  }, [user]);

  const passwordFieldErrors = apiFieldErrors(passwordState.error);
  const mismatch = passwords.confirm.length > 0 && passwords.newPassword !== passwords.confirm;

  const saveProfile = async (): Promise<void> => {
    const updated = await updateProfile(profile).unwrap().catch(() => null);
    if (!updated) return;
    const refreshed = await fetchMe().unwrap().catch(() => null);
    if (refreshed) dispatch(profileLoaded(refreshed));
  };

  const savePassword = async (): Promise<void> => {
    if (mismatch) return;
    const result = await changePassword({
      currentPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
      revokeOtherSessions: true,
    })
      .unwrap()
      .catch(() => null);

    if (result) setPasswords({ currentPassword: '', newPassword: '', confirm: '' });
  };

  return (
    <>
      <PageHeader title="Profile & security" description="Your details, password and signed-in devices." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Your details" />
          <CardBody className="space-y-4">
            {profileState.isSuccess && <Alert tone="success">Your profile has been updated.</Alert>}
            {profileState.error && <Alert tone="danger">{apiErrorMessage(profileState.error)}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="First name"
                value={profile.firstName}
                onChange={(event) => setProfile({ ...profile, firstName: event.target.value })}
              />
              <Input
                label="Last name"
                value={profile.lastName}
                onChange={(event) => setProfile({ ...profile, lastName: event.target.value })}
              />
            </div>
            <Input label="Email" value={user?.email ?? ''} disabled hint="Contact support to change your sign-in email." />
            <Input
              label="Phone"
              value={profile.phone}
              onChange={(event) => setProfile({ ...profile, phone: event.target.value })}
            />

            <Button icon={Save} onClick={() => void saveProfile()} loading={profileState.isLoading}>
              Save details
            </Button>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Change password" description="Changing your password signs out your other devices." />
          <CardBody className="space-y-4">
            {passwordState.isSuccess && <Alert tone="success">Your password has been changed.</Alert>}
            {passwordState.error && <Alert tone="danger">{apiErrorMessage(passwordState.error)}</Alert>}

            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={passwords.currentPassword}
              onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })}
              error={passwordFieldErrors.currentPassword}
            />
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              value={passwords.newPassword}
              onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}
              error={passwordFieldErrors.newPassword}
              hint="At least 10 characters with upper case, lower case and a number."
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={passwords.confirm}
              onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })}
              error={mismatch ? 'The passwords do not match' : undefined}
            />

            <Button
              onClick={() => void savePassword()}
              loading={passwordState.isLoading}
              disabled={mismatch || !passwords.currentPassword || !passwords.newPassword}
            >
              Change password
            </Button>
          </CardBody>
        </Card>

        {/* This one is about the screen, not the account, so it sits with the
            other things a person sets for themselves. */}
        <div className="lg:col-span-2">
          <AppearanceCard />
        </div>

        <div className="lg:col-span-2">
          <PrinterCard />
        </div>

        <Card className="lg:col-span-2">
          <CardHeader title="Signed-in devices" description="Revoke anything you do not recognise." />
          <CardBody>
            {sessions.isLoading ? (
              <LoadingState />
            ) : (
              <ul className="divide-y divide-[var(--border-subtle)]">
                {sessions.data?.items.map((session) => (
                  <li key={session.id} className="flex items-center justify-between gap-4 py-3">
                    <div className="flex items-center gap-3">
                      <Laptop className="size-5 text-muted" aria-hidden />
                      <div>
                        <p className="text-sm font-medium">
                          {session.deviceName ?? 'Unknown device'}
                          {session.isCurrent && <Badge tone="success" className="ml-2">This device</Badge>}
                        </p>
                        <p className="text-xs text-muted">
                          {session.ipAddress ?? 'Unknown IP'} · active {formatRelative(session.lastSeenAt, format.locale)}
                        </p>
                      </div>
                    </div>
                    {!session.isCurrent && (
                      <Button size="sm" variant="ghost" onClick={() => void revokeSession(session.id)}>
                        Revoke
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>

          {sessions.data && (
            <Pagination
              page={sessions.data.meta.page}
              totalPages={sessions.data.meta.totalPages}
              total={sessions.data.meta.total}
              onPageChange={setDevicePage}
              limit={deviceLimit}
              onLimitChange={(next) => {
                setDeviceLimit(next);
                setDevicePage(1);
              }}
            />
          )}
        </Card>
      </div>
    </>
  );
}
